/**
 * IdentityResolution — the reusable 3A component: number / name / record
 * number lookup with 0 / 1 / many outcomes and inline first-contact capture.
 * Its real host is New Booking; it takes callbacks, never navigates.
 *
 * Real contracts (ZOY-229 / ZOY-231): a mobile goes through
 * GET /patients/lookup?mobile= (contact AND caregiver numbers, server-side);
 * a short number is a record-number search; anything else is a name search.
 * "Someone without a record" creates the minimal Patient (name + contact
 * mobile) — a Guest — through the ONE patient lifecycle; there is no
 * separate guest workflow. A flagged duplicate shows the server's candidates
 * and the recorded different-person continuation for those allowed it.
 */

import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Machine, Notice, TextField } from '@/ui/kit';
import {
  fetchPatient,
  listPatients,
  lookupPatientByMobile,
  registerPatient,
  type DuplicateCandidate,
  type PatientView,
} from '@/core/clinic/patientsApi';
import type { QueryFilter } from '@/core/api/querySpec';
import { ApiError, NetworkError } from '@/core/api/errors';
import { useDebounced } from '@/core/api/useDebounced';
import { useAuthz } from '@/core/authz/AuthzProvider';
import {
  ageFromDob,
  canonicalMobile,
  classifyResolutionInput,
  duplicateCandidatesFrom,
  mobileSanityOk,
  primaryContact,
  recordNumberFromInput,
  signalLabel,
} from './lifecycle';
import { RegistrationChip } from './RegistrationChip';
import { mergeById, nameSearchSpecs } from './nameSearch';

interface Hit {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  registrationState: PatientView['registrationState'];
  recordNumber: number | null;
  contactMobile?: string;
  contactName?: string;
  signals?: string[];
}

const fromCandidate = (c: DuplicateCandidate): Hit => ({
  id: c.patientId,
  firstName: c.firstName,
  lastName: c.lastName,
  dateOfBirth: c.dateOfBirth,
  registrationState: c.registrationState,
  recordNumber: c.recordNumber,
  signals: c.signals,
});

const fromPatient = (p: PatientView): Hit => {
  const contact = primaryContact(p.caregivers);
  return {
    id: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    dateOfBirth: p.dateOfBirth,
    registrationState: p.registrationState,
    recordNumber: p.recordNumber,
    contactMobile: contact?.mobile ?? p.contactMobile,
    contactName: contact?.name,
  };
};

export function IdentityResolution(props: {
  onPick: (patient: PatientView) => void;
  /** ZOY-712: the booking desk's "no record → New follow-up" — with the searched number. */
  onNewFollowUp?: (phone: string) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const queryClient = useQueryClient();
  const canCreate = authz.can('clinic.patients.register');
  const canResolve = authz.can('clinic.patients.duplicates.resolve');
  const canFollowUp = props.onNewFollowUp !== undefined && authz.can('clinic.families.manage');

  const [input, setInput] = useState('');
  const debounced = useDebounced(input, 300);
  const kind = classifyResolutionInput(debounced);
  // ZOY-661: every stored number is canonical E.164, so the exact lookup is
  // asked ONCE with the shared normalizer's form — local, national and
  // Persian-digit spellings all fold into it.
  const mobileForm = kind === 'phone' ? canonicalMobile(debounced) : '';
  const mobileReady = kind === 'phone' && mobileSanityOk(debounced);

  const [capturing, setCapturing] = useState(false);
  const [guestFirst, setGuestFirst] = useState('');
  const [guestLast, setGuestLast] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [dupCandidates, setDupCandidates] = useState<DuplicateCandidate[] | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const filters = useMemo<QueryFilter[]>(() => {
    if (kind === 'record') {
      const n = recordNumberFromInput(debounced);
      return n === null ? [] : [{ field: 'recordNumber', op: 'eq', value: n }];
    }
    return [];
  }, [kind, debounced]);

  const lookup = useQuery({
    queryKey: ['patients-lookup', mobileForm],
    queryFn: () => lookupPatientByMobile(mobileForm),
    enabled: kind === 'phone' && mobileReady,
    staleTime: 15_000,
  });
  // ZOY-336: a name matches FIRST name as well as last (the DSL has no
  // cross-field OR, so two complete server matches merge — bounded, unpaged).
  const nameTerm = kind === 'name' ? debounced.trim() : '';
  const search = useQuery({
    queryKey: ['patients-resolve', kind, nameTerm, filters],
    queryFn: async () => {
      if (kind === 'record') {
        return listPatients({ filters, limit: 8, sort: [{ field: 'lastName', dir: 'asc' }] });
      }
      const results = await Promise.all(
        nameSearchSpecs(nameTerm).map((f) =>
          listPatients({ filters: f, limit: 8, sort: [{ field: 'lastName', dir: 'asc' }] }),
        ),
      );
      return { items: mergeById(results.map((r) => r.items)) };
    },
    enabled: (kind === 'record' && filters.length > 0) || kind === 'name',
    staleTime: 15_000,
  });

  const active =
    kind === 'phone' ? (mobileReady ? lookup : null) : filters.length > 0 ? search : null;
  const hits: Hit[] =
    kind === 'phone'
      ? (lookup.data?.candidates ?? []).map(fromCandidate)
      : (search.data?.items ?? []).map(fromPatient);

  const [pickingId, setPickingId] = useState<string | null>(null);
  const pick = async (id: string) => {
    setPickingId(id);
    try {
      const full = await queryClient.fetchQuery({
        queryKey: ['patient', id],
        queryFn: () => fetchPatient(id),
        staleTime: 60_000,
      });
      props.onPick(full);
    } finally {
      setPickingId(null);
    }
  };

  const create = useMutation({
    mutationFn: (confirmDifferentPerson?: boolean) =>
      registerPatient({
        firstName: guestFirst.trim(),
        lastName: guestLast.trim(),
        // ZOY-336: one canonical stored form (E.164 when confidently Iranian).
        contactMobile: canonicalMobile(guestPhone),
        ...(confirmDifferentPerson ? { confirmDifferentPerson: true } : {}),
      }),
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.setQueryData(['patient', p.id], p);
      props.onPick(p);
    },
    onError: (err) => {
      const candidates = duplicateCandidatesFrom(err);
      if (candidates !== null) {
        setDupCandidates(candidates);
        return;
      }
      if (err instanceof NetworkError) setCreateError(t('signin.netBody') as string);
      else if (err instanceof ApiError && err.status === 403)
        setCreateError(t('pt.errForbidden') as string);
      else if (err instanceof ApiError && err.serverMessage) setCreateError(err.serverMessage);
      else setCreateError(t('common.failedKept') as string);
    },
  });

  const renderHit = (h: Hit, action: React.ReactNode) => {
    const age = ageFromDob(h.dateOfBirth);
    return (
      <div key={h.id} className={a.row}>
        <span className={a.rowValue}>
          <strong style={{ color: 'var(--zc-ink-strong)' }}>
            {h.firstName} {h.lastName}
          </strong>{' '}
          <RegistrationChip state={h.registrationState} recordNumber={h.recordNumber} compact />
          <span style={{ display: 'block', fontSize: 12, color: 'var(--zc-muted)' }}>
            {age !== null && t('pt.age', { n: age })}
            {h.contactMobile && (
              <>
                {age !== null ? ' · ' : ''}
                <Machine>{h.contactMobile}</Machine>
                {h.contactName ? ` — ${h.contactName}` : ''}
              </>
            )}
            {h.signals && h.signals.length > 0 && (
              <>
                {' · '}
                {h.signals.map((sig) => signalLabel(intl, sig)).join(' · ')}
              </>
            )}
          </span>
        </span>
        <span className={a.rowAction}>{action}</span>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--zc-teal-deep)' }}>
          {t('res.title')}
        </h2>
        <p
          style={{
            fontSize: 12.5,
            color: 'var(--zc-ink-2)',
            marginBlockStart: 4,
            maxInlineSize: '62ch',
          }}
        >
          {t('res.sub')}
        </p>
      </div>

      <TextField
        label={t('res.title')}
        value={input}
        onChange={setInput}
        help={t('res.searchPh')}
        machine={kind === 'phone' || kind === 'record'}
        autoFocus
      />

      {kind === 'phone' && !mobileReady && (
        <span style={{ fontSize: 12, color: 'var(--zc-muted)' }}>{t('res.moreDigits')}</span>
      )}

      {active?.isError && (
        <Notice
          kind="err"
          action={<Button onClick={() => void active.refetch()}>{t('rec.retry')}</Button>}
        >
          {t('rec.sectionFailed')}
        </Notice>
      )}

      {hits.length > 0 && (
        <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
          {hits.map((h) =>
            renderHit(
              h,
              <Button
                variant="brand"
                busy={pickingId === h.id}
                disabled={pickingId !== null}
                onClick={() => void pick(h.id)}
              >
                {t('res.pick')}
              </Button>,
            ),
          )}
        </div>
      )}

      {active && !active.isPending && !active.isError && hits.length === 0 && (
        <Notice kind="info">{t('res.noMatches')}</Notice>
      )}

      {!capturing ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--zc-muted)' }}>{t('res.none')}</span>
          {canCreate ? (
            <Button
              variant="quiet"
              onClick={() => {
                setCapturing(true);
                setDupCandidates(null);
                setCreateError(null);
                if (kind === 'phone') setGuestPhone(canonicalMobile(debounced));
                if (kind === 'name') setGuestLast(debounced.trim());
              }}
            >
              {t('res.newCta')}
            </Button>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--zc-muted)' }}>{t('res.needRegister')}</span>
          )}
          {canFollowUp && kind === 'phone' && mobileReady && (
            <Button
              variant="quiet"
              onClick={() => props.onNewFollowUp!(canonicalMobile(debounced))}
            >
              {t('crm.res.new')}
            </Button>
          )}
        </div>
      ) : (
        <div className={a.sectionCard} style={{ inlineSize: '100%', padding: '16px 18px' }}>
          <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('res.guestTitle')}</strong>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBlockStart: 10 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minInlineSize: 160 }}>
                <TextField
                  label={t('ptForm.first')}
                  value={guestFirst}
                  onChange={setGuestFirst}
                  requirement={{ word: t('signin.required'), kind: 'required' }}
                />
              </div>
              <div style={{ flex: 1, minInlineSize: 160 }}>
                <TextField
                  label={t('ptForm.last')}
                  value={guestLast}
                  onChange={setGuestLast}
                  help={t('res.guestNameHelp')}
                  requirement={{ word: t('signin.required'), kind: 'required' }}
                />
              </div>
            </div>
            <TextField
              label={t('res.guestPhone')}
              value={guestPhone}
              onChange={setGuestPhone}
              help={t('res.guestPhoneHelp')}
              requirement={{ word: t('signin.required'), kind: 'required' }}
              error={
                guestPhone.trim() !== '' && !mobileSanityOk(guestPhone)
                  ? (t('ptForm.mobileTooShort') as string)
                  : undefined
              }
              machine
              type="tel"
            />

            {createError && (
              <Notice kind="err" role="alert">
                {createError}
              </Notice>
            )}

            {dupCandidates !== null && (
              <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
                <div style={{ padding: '10px 14px 0' }}>
                  <strong style={{ color: 'var(--zc-warn-ink)' }}>{t('dup.title')}</strong>
                  <p style={{ fontSize: 12.5, color: 'var(--zc-ink-2)', marginBlockStart: 2 }}>
                    {t('dup.askCaller')}
                  </p>
                </div>
                {dupCandidates.map((c) =>
                  renderHit(
                    fromCandidate(c),
                    <Button
                      variant="brand"
                      busy={pickingId === c.patientId}
                      disabled={pickingId !== null}
                      onClick={() => void pick(c.patientId)}
                    >
                      {t('dup.same')}
                    </Button>,
                  ),
                )}
                <div style={{ padding: '8px 14px 12px' }}>
                  {canResolve ? (
                    <Button
                      variant="quiet"
                      busy={create.isPending}
                      disabled={create.isPending}
                      onClick={() => create.mutate(true)}
                    >
                      {t('dup.different')}
                    </Button>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--zc-muted)' }}>
                      {t('dup.needResolve')}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="primary"
                busy={create.isPending}
                disabled={
                  create.isPending ||
                  guestFirst.trim() === '' ||
                  guestLast.trim() === '' ||
                  !mobileSanityOk(guestPhone)
                }
                onClick={() => {
                  setDupCandidates(null);
                  setCreateError(null);
                  create.mutate(undefined);
                }}
              >
                {t('res.guestCta')}
              </Button>
              <Chip kind="warn">{t('pt.stateGuest')}</Chip>
            </div>
            <span style={{ fontSize: 12, color: 'var(--zc-muted)' }}>{t('res.guestNote')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
